// Module
import { useLocation } from "react-router-dom";

// components
import { Button, Form, Input } from "antd";
import Label from "components/atoms/Label/Label";
import getIcon from "utils/getIcon";

// constants
import { ReimbursementVerificationReview as Constants, Routes } from "constants/global";

// Utils
import { getVerifierStatus } from "utils/Generic/generic";
// styles
import styles from "components/organisms/ReimbursementVerificationReviewDetails/reimbursementVerificationReviewDetails.module.css";

type ReimbursementDetailsTypes = {
	claimDetails: {
		verifier_remarks: string;
		tenant_name: string;
		outlet_name: string;
		net_amount: string;
		tenant_amount: string;
		tenant_remarks: string;
		reimb_status: string;
		verifier_rejected: string;
		reimb_amount: number;
	};
	documents: string[];
	handleDownload?: (val: string) => void;
};

const getFileInfo = (url: string) => {
	return url?.split("/")?.at(-1);
};

const ReimbursementVerificationReviewDetails = ({ claimDetails, documents, handleDownload }: ReimbursementDetailsTypes) => {
	const { pathname } = useLocation();
	const isStatusApproved = claimDetails?.reimb_status?.toLowerCase() === "approved";
	let labelClass = styles?.status_label;
	if (isStatusApproved) {
		labelClass = styles?.status_approved;
	} else if (claimDetails?.reimb_status?.toLowerCase() === "rejected") {
		labelClass = styles?.status_rejected;
	}
	const isVerifierStatusApproved = claimDetails?.verifier_rejected?.toLowerCase() === "";
	let labelClassVerifier = styles?.status_label;
	if (isVerifierStatusApproved) {
		labelClassVerifier = styles?.status_approved;
	} else if (claimDetails?.verifier_rejected?.toLowerCase() === "x") {
		labelClassVerifier = styles?.status_rejected;
	}

	return (
		<Form className={styles?.form} requiredMark={false} layout="vertical">
			<div className={styles?.field_wrapper}>
				<div className="flex flex-col md:flex-row">
					{pathname.includes(Routes.DECLARATION_APPROVAL) && (
						<Form.Item name={Constants.FIELD_STATUS} label={<Label label={Constants.VERIFIER_LABEL} classes={{ label: styles?.step_input_label }} />}>
							<Label label={getVerifierStatus(claimDetails.verifier_rejected)} classes={{ label: labelClassVerifier }}></Label>
						</Form.Item>
					)}
					<Form.Item name={Constants.FIELD_STATUS} label={<Label label={Constants.STATUS_LABEL} classes={{ label: styles?.step_input_label }} />}>
						<Label label={claimDetails?.reimb_status?.replace("_", " ")} classes={{ label: labelClass }}></Label>
					</Form.Item>
				</div>
				<div className="flex flex-col md:flex-row">
					<Form.Item
						name={Constants.FIELD_TENANT}
						initialValue={claimDetails?.tenant_name}
						label={<Label label={Constants.TENANT_LABEL} classes={{ label: styles?.step_input_label }} />}
					>
						<Input
							title={claimDetails?.tenant_name}
							data-testid={Constants.TENANT_LABEL}
							readOnly={true}
							placeholder={Constants.TENANT_LABEL}
							className={`${styles?.step_input}`}
						/>
					</Form.Item>
					<Form.Item
						name={Constants.FIELD_OUTLET}
						initialValue={claimDetails?.outlet_name}
						label={<Label label={Constants.OUTLET_LABEL} classes={{ label: styles?.step_input_label }} />}
					>
						<Input
							title={claimDetails?.outlet_name}
							data-testid={Constants.OUTLET_LABEL}
							readOnly={true}
							placeholder={Constants.OUTLET_LABEL}
							className={`${styles?.step_input}`}
						/>
					</Form.Item>
				</div>
				<div className="flex flex-col md:flex-row">
					<Form.Item
						name={Constants.FIELD_NET_AMOUNT}
						initialValue={claimDetails?.net_amount}
						label={<Label label={Constants.NET_AMOUNT_LABEL} classes={{ label: styles?.step_input_label }} />}
					>
						<Input data-testid={Constants.NET_AMOUNT_LABEL} readOnly={true} placeholder={Constants.NET_AMOUNT_LABEL} className={`${styles?.step_input}`} />
					</Form.Item>
					<Form.Item
						name={Constants.FIELD_DECLARED_AMOUNT}
						initialValue={claimDetails?.tenant_amount}
						label={<Label label={Constants.DECLARED_AMOUNT_LABEL} classes={{ label: styles?.step_input_label }} />}
					>
						<Input data-testid={Constants.DECLARED_AMOUNT_LABEL} readOnly={true} placeholder={Constants.DECLARED_AMOUNT_LABEL} className={`${styles?.step_input}`} />
					</Form.Item>
					{pathname.includes(Routes.DECLARATION_APPROVAL) && (
						<Form.Item
							name={Constants.FIELD_REIMBURSEMENT_AMOUNT}
							initialValue={claimDetails?.reimb_amount}
							label={<Label label={Constants.REIMBURSEMENT_AMOUNT_LABEL} classes={{ label: styles?.step_input_label }} />}
						>
							<Input data-testid={Constants.REIMBURSEMENT_AMOUNT_LABEL} readOnly={true} placeholder={Constants.REIMBURSEMENT_AMOUNT_LABEL} className={`${styles?.step_input}`} />
						</Form.Item>
					)}
				</div>
				<div className="w-full flex flex-col md:flex-row">
					<Form.Item
						style={{ width: "inherit" }}
						name={Constants.FIELD_TENANT_REMARK}
						initialValue={claimDetails?.tenant_remarks}
						label={<Label label={Constants.TENANT_REMARK_LABEL} classes={{ label: styles?.step_input_label }} />}
					>
						<Input.TextArea data-testid={Constants.TENANT_REMARK_LABEL} readOnly={true} placeholder={Constants.TENANT_REMARK_LABEL} className={`${styles?.step_input_textarea}`} />
					</Form.Item>
				</div>
				{pathname.includes(Routes.DECLARATION_APPROVAL) && (
					<div className="w-full flex flex-col md:flex-row">
						<Form.Item
							style={{ width: "inherit" }}
							name={Constants.FIELD_VERIFIER_REMARK}
							initialValue={claimDetails?.verifier_remarks}
							label={<Label label={Constants.VERIFIER_REMARK_LABEL} classes={{ label: styles?.step_input_label }} />}
						>
							<Input.TextArea
								data-testid={Constants.VERIFIER_REMARK_LABEL}
								readOnly={true}
								placeholder={Constants.VERIFIER_REMARK_LABEL}
								className={`${styles?.step_input_textarea}`}
							/>
						</Form.Item>
					</div>
				)}
				<div className={styles?.download_button_wrapper}>
					{documents?.map((document, index) => {
						const filename = getFileInfo(document);
						return (
							<Button
								onClick={() => handleDownload?.(document)}
								key={`${document}-${index}`}
								className={styles?.download_button}
								icon={<span className="inline">{getIcon({ icon: "download" })}</span>}
							>
								<span className={styles?.download_button_text}>{filename}</span>
							</Button>
						);
					})}
				</div>
			</div>
		</Form>
	);
};

export default ReimbursementVerificationReviewDetails;
